<script lang="ts">
  import { Link, navigate } from 'svelte-routing';
  import { auth, isAuthenticated, currentUser, acting } from '../stores/auth';
  import { api } from '../api/client';
  import { t } from '../i18n/index.ts';

  let showUserMenu = $state(false);

  async function handleLogout() {
    await auth.logout();
    navigate('/login');
  }

  let user = $derived($currentUser);

  // Devices is offered once the account has two or more devices (spec
  // 011 FR-001).
  let deviceCount = $state(0);
  $effect(() => {
    if (!$isAuthenticated) {
      deviceCount = 0;
      return;
    }
    api.get<unknown[]>('/me/devices').then((ds) => (deviceCount = ds.length)).catch(() => {});
  });

  // doneActing ends a parent's acting session and closes the tab it opened.
  async function doneActing() {
    await auth.logout();
    window.close();
    window.location.href = '/settings';
  }
</script>

{#if $acting && user}
  <nav class="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 px-4 sm:px-6 py-3 border-b"
       style="background-color: var(--theme-surface0); border-color: var(--theme-accent);">
    <div class="flex flex-wrap items-center gap-x-6 gap-y-2">
      <span class="font-semibold"><i aria-hidden="true" class="las la-child mr-1"></i>{t('componentsNavManaging', { child: user.display_name })}</span>
      <div class="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
        <Link to="/settings" class="hover:opacity-80"><i aria-hidden="true" class="las la-cog mr-1"></i>{t('appSettings')}</Link>
        <Link to="/groups" class="hover:opacity-80"><i aria-hidden="true" class="las la-users mr-1"></i>{t('componentsNavGroups')}</Link>
        {#if deviceCount >= 2}
          <Link to="/devices" class="hover:opacity-80"><i aria-hidden="true" class="las la-mobile mr-1"></i>{t('componentsNavDevices')}</Link>
        {/if}
      </div>
    </div>
    <button class="btn btn-secondary text-sm px-3 py-1" onclick={doneActing}>{t('appDone')}</button>
  </nav>
{:else}
<nav class="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 px-4 sm:px-6 py-3 border-b"
     style="background-color: var(--theme-nav-bg, var(--theme-mantle)); border-color: var(--theme-border, var(--theme-surface0));">
  <div class="flex flex-wrap items-center gap-x-6 gap-y-2">
    <Link to="/" class="flex shrink-0"><img src="/favicon.svg" alt={t('componentsNavLogoAlt')} width="32" height="32" class="rounded-lg" /></Link>

    {#if $isAuthenticated}
      <div class="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
        <Link to="/" class="hover:opacity-80"><i aria-hidden="true" class="las la-map-marked-alt mr-1"></i>{t('componentsNavMap')}</Link>
        <Link to="/places" class="hover:opacity-80"><i aria-hidden="true" class="las la-map-pin mr-1"></i>{t('componentsNavPlaces')}</Link>
        <Link to="/timeline/me" class="hover:opacity-80"><i aria-hidden="true" class="las la-stream mr-1"></i>{t('componentsNavTimeline')}</Link>
        <Link to="/activities" class="hover:opacity-80"><i aria-hidden="true" class="las la-running mr-1"></i>{t('componentsNavActivities')}</Link>
        <Link to="/check-in" class="hover:opacity-80"><i aria-hidden="true" class="las la-stopwatch mr-1"></i>{t('componentsNavCheckIn')}</Link>
        {#if deviceCount >= 2}
          <Link to="/devices" class="hover:opacity-80"><i aria-hidden="true" class="las la-mobile mr-1"></i>{t('componentsNavDevices')}</Link>
        {/if}
        <Link to="/groups" class="hover:opacity-80"><i aria-hidden="true" class="las la-users mr-1"></i>{t('componentsNavGroups')}</Link>
      </div>
    {/if}
  </div>

  <div class="flex items-center gap-3">
    {#if $isAuthenticated && user}
      <div class="relative">
        <button
          class="btn btn-secondary text-sm px-3 py-1"
          onclick={(e: MouseEvent) => { e.stopPropagation(); showUserMenu = !showUserMenu; }}
        >
          <i aria-hidden="true" class="las la-user-circle mr-1"></i>{user.display_name}
        </button>
        {#if showUserMenu}
          <div class="absolute right-0 top-full mt-1 card p-2 min-w-36 z-50">
            <Link to="/settings" class="block px-3 py-2 rounded text-sm hover:opacity-80">
              <i aria-hidden="true" class="las la-cog mr-1"></i>{t('appSettings')}
            </Link>
            <button
              class="block w-full text-left px-3 py-2 rounded text-sm hover:opacity-80"
              onclick={handleLogout}
            >
              <i aria-hidden="true" class="las la-sign-out-alt mr-1"></i>{t('componentsNavSignOut')}
            </button>
          </div>
        {/if}
      </div>
    {/if}
  </div>
</nav>
{/if}

<svelte:window onclick={() => { showUserMenu = false; }} />
